import { useCallback, useEffect } from 'react'

import { useUnit } from 'effector-react'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import SortIcon from '@/assets/icons/icon-sort.svg'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $hasEditVaultPermission } from '@/models/permissions.ts'
import {
  $categoryOrder,
  $categorySort,
  getVaultCategorySortFx,
  setIsCustomCategory,
  setVaultCategorySortFx,
} from '@/models/vault/categoryOptions.ts'
import { classNames } from '@/utils/helpers.ts'

import { VaultCategoriesSearch } from './vault-categories-search'

interface Props {
  showSort?: boolean
  showSearch?: boolean
}

export const VaultCategoriesSort = ({
  showSort = true,
  showSearch = false,
}: Props) => {
  const creatorId = useUnit($creatorId)
  const categorySort = useUnit($categorySort)
  const categoryOrder = useUnit($categoryOrder)
  const isUpdateSortLoading = useUnit(getVaultCategorySortFx.pending)
  const hasEditVaultPermission = useUnit($hasEditVaultPermission)
  const handleEnableCustomCategory = useCallback(
    () => setIsCustomCategory(true),
    [],
  )

  useEffect(() => {
    if (creatorId) {
      getVaultCategorySortFx()
    }
  }, [creatorId])

  const handleSortChange = useCallback(
    (sort: string) => {
      setVaultCategorySortFx({
        sort: sort ?? categorySort ?? 'recent',
        order: categoryOrder ?? 'asc',
      })
    },
    [categoryOrder, categorySort],
  )

  const handleOrderChange = useCallback(
    (order: string) => {
      setVaultCategorySortFx({
        sort: categorySort ?? 'recent',
        order: order ?? categoryOrder ?? 'asc',
      })
    },
    [categoryOrder, categorySort],
  )

  const handleCreateCategoryClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.VAULT_ADD_CATEGORY,
    })
  }, [])

  return (
    <div
      className={classNames('flex items-center px-2.5 pb-4 lg:h-[52px]', {
        'w-full': showSort,
      })}
    >
      {showSort && (
        <p className="text-xl font-medium capitalize text-secondary-500 dark:text-white">
          {categorySort}
        </p>
      )}
      {showSearch && (
        <div className="pl-6 pr-2 ml-auto">
          <VaultCategoriesSearch />
        </div>
      )}
      {hasEditVaultPermission && (
        <Tooltip
          content="Create New Category"
          className="flex items-center"
          asChild
        >
          <Button
            onClick={handleCreateCategoryClick}
            variant="ghost"
            className="h-auto py-1 text-text-default"
          >
            <PlusIcon className="h-6 w-6" />
          </Button>
        </Tooltip>
      )}
      <DropdownMenu>
        <Tooltip
          content="Sort"
          className="flex items-center"
          asChild
        >
          <DropdownMenuTrigger disabled={isUpdateSortLoading}>
            <div
              className={classNames('cursor-pointer p-1', {
                'cursor-not-allowed opacity-50': isUpdateSortLoading,
              })}
            >
              <SortIcon className="dark:stroke-white" />
            </div>
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuPortal>
          <DropdownMenuContent
            className="z-[100] max-h-[400px] w-64 overflow-y-auto p-2"
            align="start"
            forceMount
          >
            {SORT_OPTIONS.map((option) => {
              const isActive = categorySort === option.value

              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleSortChange(option.value)}
                  className={classNames(
                    isActive &&
                      'bg-primary-500 text-white hover:bg-primary-500',
                  )}
                >
                  {option.label}
                </DropdownMenuItem>
              )
            })}
            <DropdownMenuSeparator />
            {ORDER_OPTIONS.map((option) => {
              const isActive = categoryOrder === option.value

              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleOrderChange(option.value)}
                  className={classNames(
                    isActive &&
                      'bg-primary-500 text-white hover:bg-primary-500',
                  )}
                >
                  {option.label}
                </DropdownMenuItem>
              )
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleEnableCustomCategory}>
              Customize order
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
    </div>
  )
}

const SORT_OPTIONS = [
  { label: 'Default', value: 'default' },
  { label: 'Name', value: 'name' },
  { label: 'Recent', value: 'recent' },
  { label: 'Media', value: 'media' },
]

const ORDER_OPTIONS = [
  { label: 'Ascending', value: 'asc' },
  { label: 'Descending', value: 'desc' },
]
